import React from 'react';
import { motion } from 'framer-motion';
import { Phone, FileText, Hammer, Rocket } from 'lucide-react';

const Process = () => {
  const steps = [
    {
      icon: Phone,
      number: '01',
      title: 'Discovery Call',
      duration: '15 min',
      points: [
        'Understand your needs',
        'Share relevant examples',
        'Provide ballpark estimate',
      ],
    },
    {
      icon: FileText,
      number: '02',
      title: 'Proposal & Agreement',
      duration: '24 hours',
      points: [
        'Detailed scope & pricing',
        'Fixed timeline commitment',
        '50% advance payment',
      ],
    },
    {
      icon: Hammer,
      number: '03',
      title: 'Build & Iterate',
      duration: '2-8 weeks',
      points: [
        'Weekly demos',
        'Your feedback incorporated',
        'Direct WhatsApp access',
      ],
    },
    {
      icon: Rocket,
      number: '04',
      title: 'Launch & Support',
      duration: 'Ongoing',
      points: [
        'Deployment & training',
        '2 weeks post-launch support',
        'Optional maintenance plans',
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section id="process" className="section-padding bg-[#f8fafc]" aria-label="Our Process" data-testid="process-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#ff6d00] mb-3 block">
            Process
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="process-headline"
          >
            Our Process: Simple & Transparent
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            From discovery to deployment in four clear steps
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative"
              data-testid={`process-step-${index}`}
            >
              {/* Connector Line (hidden on mobile and last item) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-[#00e5ff] to-[#1a237e]/30 z-0" />
              )}
              
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 relative z-10 h-full">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00e5ff]/20 to-[#1a237e]/20 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-[#1a237e]" />
                  </div>
                  <span className="text-4xl font-bold text-[#1a237e]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <h3
                  className="text-lg md:text-xl font-semibold text-[#212121] mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {step.title}
                </h3>
                <span className="inline-block px-3 py-1 bg-[#00e5ff]/10 text-[#1a237e] text-xs font-medium rounded-full mb-4">
                  {step.duration}
                </span>
                <ul className="space-y-2">
                  {step.points.map((point, i) => (
                    <li key={i} className="text-sm text-[#5a6b8c] flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] mt-1.5 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
